import React, { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Calendar, 
  Users, 
  Clock, 
  TrendingUp, 
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import { Reservation } from '../../types';
import apiService from '../../services/api';
import ReservationCard from '../reservations/ReservationCard';
import RestaurantInfo from '../reservations/RestaurantInfo';
import NotificationPrompt from '../common/NotificationPrompt';

const Dashboard: React.FC = () => {
  const { restaurant, stats, isLoading: restaurantLoading } = useRestaurant();
  const { user } = useAuth();
  const [todayReservations, setTodayReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTodayReservations = useCallback(async () => {
    if (!restaurant?.id) {
      setIsLoading(false);
      setTodayReservations([]); // Ensure empty array when no restaurant
      return;
    }

    try {
      setIsLoading(true);
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      const reservations = await apiService.getReservationsByRestaurantAndDate(restaurant.id, today);
      console.log('API response for today reservations:', reservations, 'Type:', typeof reservations, 'IsArray:', Array.isArray(reservations));
      
      // Ensure we always set an array and filter out canceled/failed payments
      if (Array.isArray(reservations)) {
        // Filter out canceled reservations and those with failed payments
        const filteredReservations = reservations.filter(reservation => 
          reservation.status !== 'cancelada' && 
          reservation.payment_status !== 'failed'
        );
        setTodayReservations(filteredReservations);
      } else {
        console.warn('API returned non-array data:', reservations);
        setTodayReservations([]);
      }
    } catch (error) {
      console.error('Failed to fetch today\'s reservations:', error);
      setTodayReservations([]); // Ensure it's always an empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => {
    if (restaurant?.id) {
      fetchTodayReservations();
    } else {
      // Ensure we always have an empty array when no restaurant
      setTodayReservations([]);
      setIsLoading(false);
    }
  }, [restaurant?.id, fetchTodayReservations]);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await apiService.updateReservationStatus(id, { status: status as any });
      await fetchTodayReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to update reservation status:', error);
    }
  };

  const handleCancelReservation = async (id: number) => {
    try {
      await apiService.cancelReservation(id);
      await fetchTodayReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
    }
  };

  const handleRefundReservation = async (id: number) => {
    try {
      await fetchTodayReservations(); // Refresh the list to show updated payment status
    } catch (error) {
      console.error('Failed to refresh reservations after refund:', error);
    }
  };

  // FORCE safe array creation - never trust todayReservations directly
  const safeReservations = (() => {
    if (todayReservations === null || todayReservations === undefined) {
      console.log('todayReservations is null/undefined, using empty array');
      return [];
    }
    if (!Array.isArray(todayReservations)) {
      console.error('todayReservations is not an array:', todayReservations, 'Type:', typeof todayReservations);
      return [];
    }
    return todayReservations;
  })();
  
  // Debug logging
  console.log('Original todayReservations:', todayReservations, 'type:', typeof todayReservations, 'isArray:', Array.isArray(todayReservations));
  console.log('Safe reservations:', safeReservations, 'type:', typeof safeReservations, 'isArray:', Array.isArray(safeReservations), 'length:', safeReservations.length);
  
  // Calculate stats using ONLY the safe array
  const todayStats = (() => {
    try {
      const confirmed = safeReservations.filter(r => r && r.status === 'confirmada').length;
      const pending = safeReservations.filter(r => r && r.status === 'pendente').length;
      const cancelled = safeReservations.filter(r => r && r.status === 'cancelada').length;
      const total = safeReservations.length;
      
      return { total, confirmed, pending, cancelled };
    } catch (error) {
      console.error('Error calculating stats:', error);
      return { total: 0, confirmed: 0, pending: 0, cancelled: 0 };
    }
  })();

  const statCards = [
    {
      title: 'Reservas Hoje',
      value: todayStats.total,
      icon: Calendar,
      color: 'bg-blue-500',
      change: null,
    },
    {
      title: 'Confirmadas',
      value: todayStats.confirmed,
      icon: CheckCircle,
      color: 'bg-green-500',
      change: null,
    },
    {
      title: 'Pendentes',
      value: todayStats.pending,
      icon: Clock,
      color: 'bg-yellow-500',
      change: null,
    },
    {
      title: 'Taxa Ocupação',
      value: stats?.taxa_ocupacao != null && typeof stats.taxa_ocupacao === 'number' ? `${stats.taxa_ocupacao.toFixed(1)}%` : '-',
      icon: TrendingUp,
      color: 'bg-purple-500',
      change: null,
    },
  ];

  // EMERGENCY FALLBACK - if todayReservations is corrupted, show loading
  if (restaurantLoading || isLoading || !Array.isArray(todayReservations)) {
    if (!Array.isArray(todayReservations) && !isLoading && !restaurantLoading) {
      console.error('EMERGENCY: todayReservations is not an array, forcing reload:', todayReservations);
    }
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
          <p className="mt-2 text-sm text-secondary-600">
            {!Array.isArray(todayReservations) && !isLoading && !restaurantLoading 
              ? 'Erro de dados - A recarregar...' 
              : 'A carregar dashboard...'
            }
          </p>
          {!Array.isArray(todayReservations) && !isLoading && !restaurantLoading && (
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 btn-primary text-sm"
            >
              Recarregar Página
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Bem-vindo, {user?.nome}! Aqui está o resumo do seu restaurante.
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            onClick={fetchTodayReservations}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Notification Prompt */}
      <NotificationPrompt />

      {/* Restaurant Context Information */}
      <RestaurantInfo />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-secondary-500 truncate">
                    {stat.title}
                  </dt>
                  <dd className="text-lg font-semibold text-secondary-900">
                    {stat.value}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Restaurant Info */}
      {restaurant && (
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4">
            Informações do Restaurante
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-secondary-700 mb-2">Contacto</h3>
              <div className="space-y-1 text-sm text-secondary-600">
                {restaurant.contacto.telefone && (
                  <p>Telefone: {restaurant.contacto.telefone}</p>
                )}
                {restaurant.contacto.email && (
                  <p>Email: {restaurant.contacto.email}</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-secondary-700 mb-2">Localização</h3>
              <div className="text-sm text-secondary-600">
                <p>{restaurant.localizacao.morada}</p>
                {restaurant.localizacao.codigo_postal && restaurant.localizacao.cidade && (
                  <p>{restaurant.localizacao.codigo_postal} {restaurant.localizacao.cidade}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Reservations */}
      <div className="card p-6">
        <h2 className="text-lg font-medium text-secondary-900 mb-4">
          Reservas de Hoje
        </h2>
        
        {safeReservations.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-secondary-500">Não há reservas para hoje.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {safeReservations.map((reservation) => (
              <ReservationCard
                key={reservation.id}
                reservation={reservation}
                onStatusChange={handleStatusChange}
                onCancel={handleCancelReservation}
                onRefund={handleRefundReservation}
              />
            ))}
          </div>
        )}
      </div>

      {/* Monthly Stats */}
      {stats && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">
                    Total Reservas (30 dias)
                  </p>
                  <p className="text-2xl font-semibold text-secondary-900">
                    {stats?.total_reservations || 0}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">
                    Clientes Únicos
                  </p>
                  <p className="text-2xl font-semibold text-secondary-900">
                    {stats?.clientes_unicos || 0}
                  </p>
                </div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">
                    Horário Pico
                  </p>
                  <p className="text-2xl font-semibold text-secondary-900">
                    {stats?.horario_pico || '--:--'}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default Dashboard;
